import { action, computed, observable } from 'mobx';
import { LoyaltyApiSdk } from '~api/LoyaltyApiSdk';
import { LoyaltyRpcAPI } from '~api/RpcAPI/LoyaltyRpcAPI';
import LoyaltyInfo from '~domain/entities/LoyaltyInfo';
import Benefits from '~domain/entities/LoyaltyBenefitsInfo';
import Rewards from '~domain/entities/LoyaltyRewardsInfo';
import PointsHistory from '~domain/entities/LoyaltyPointsHistoryInfo';
import { IBirthDate } from '~exported/loyalty-birthday/ILoyaltyBirthday';
import { ILoyaltyApiResponse } from '~api/interfaces/ILoyaltyApiResponse';
import { IPointsHistoryResponse } from '~api/interfaces/IPointsHistoryResponse';
import { ILoyaltyRewardsResponse } from '~api/interfaces/ILoyaltyRewardsResponse';
import { ILoyaltyBenefitsResponse } from '~api/interfaces/ILoyaltyBenefitsResponse';

import {
    ILoyaltyEnrollmentResponse,
    IUserLoyaltyEnrollment
} from '~api/interfaces/ILoyaltyEnrollmentResponse';
import { AuthenticatedRepository } from './AuthenticatedRepository';
import {
    ILoyaltyOfferResponse,
    ILoyaltyOfferResponseModifiedResponse,
    ILoyaltyRedeemOffersRequest,
    ILoyaltyRedeemOfferRequest,
    ILoyaltyUnredeemOfferApiRequest
} from '~api/interfaces/ILoyaltyCartAPI';
import { BenefitsOffer, IPGResponse, ILoyaltyRPCRequest  } from '~api/interfaces/ILoyaltyRpcAPI';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { AnyObject } from '@estee/elc-universal-utils';

export enum offerRedemptionType {
    benefits,
    rewards,
    birthday
}

export interface IOfferRedemptionMessage {
    redemptionAction: 'applied' | 'removed';
    content: string[];
    section: 'benefits' | 'birthday' | 'rewards';
    type: 'error' | 'info' | 'success';
}

export interface IOfferRedemptionMessages {
    'benefits'?: IOfferRedemptionMessage;
    'birthday'?: IOfferRedemptionMessage;
    'rewards'?: IOfferRedemptionMessage;
}

export class LoyaltyRepository extends AuthenticatedRepository {
    private apiSdk: LoyaltyApiSdk;
    private logger: ELCLogger;
    private rpcApi: LoyaltyRpcAPI;
    private enableCartGraphqlAPI: boolean = true;

    @observable private loyaltyData: LoyaltyInfo;
    @observable private pointHistoryData: PointsHistory;
    @observable private benefitData: Benefits;
    @observable private rewardsData: Rewards;
    @observable private birthdayRewardsData: Rewards;
    @observable private benefitRedeemOfferData: BenefitsOffer;

    @observable private rewardRedeemed: boolean;
    @observable private birthdayRedeemed: boolean;

    @observable private offerRedemptionMessages: IOfferRedemptionMessages;

    // Points History Pagination Config
    public enablePagination: boolean = true;
    private loadMoreLimit: number = 5;
    @observable private pointsHistoryPaginationData: PointsHistory;
    @observable public paginationLimit: number = 0;

    constructor(graphQLAPI: LoyaltyApiSdk, logger: ELCLogger, rpcAPI: LoyaltyRpcAPI) {
        super(graphQLAPI);
        this.apiSdk = graphQLAPI;
        this.rpcApi = rpcAPI;
        this.logger = logger;
        this.rewardRedeemed = false;
        this.birthdayRedeemed = false;
        this.offerRedemptionMessages = {};
    }

    @action public fetchLoyaltyInfo = async () => {
        return super.ensureData();
    };

    @action public fetchData = async () => {
        await this.fetchLoyaltyUserData();
    };

    @action public updateLoadingStatus = () => {
        super.resetStatus();
    };

    @action public postEnrollmentData = async (loyaltyEnrollmentData: IUserLoyaltyEnrollment) => {
        const data: ILoyaltyEnrollmentResponse = await this.apiSdk.userLoyaltyEnrollment(
            loyaltyEnrollmentData
        );

        return data;
    };

    @action public fetchLoyaltyUserData = async () => {
        try {
            const loyaltyUserData: ILoyaltyApiResponse = await this.apiSdk.getLoyaltyUserData();
            const userData = LoyaltyInfo.fromJSON(loyaltyUserData);
            this.setLoyaltyData(userData);
        } catch (err) {
            const errorMessage = err[0]?.message || '';
            this.logger.error({
                message: `Loyalty user info API error : ${errorMessage}`,
                triggerType: TriggerType.api
            });
        }
    };

    @action public fetchPointsHistory = async () => {
        try {
            const pointsHistoryData: IPointsHistoryResponse =
                await this.apiSdk.getPointsHistoryData();
            const pointHistoryData = PointsHistory.fromJSON(pointsHistoryData);
            this.setPointHistory(pointHistoryData);
        } catch (err) {
            const errorMessage = err[0]?.message || '';
            this.logger.error({
                message: `Points history API error : ${errorMessage}`,
                triggerType: TriggerType.api
            });
        }
    };

    @action public fetchRewardsData = async (rewardOfferView: string) => {
        try {
            const rewardOfferData: ILoyaltyRewardsResponse =
                await this.apiSdk.getLoyaltyRewardOffers(rewardOfferView);

            const { levelValue } = this.loyaltyData;

            const modifiedRewardsOfferData = {
                ...rewardOfferData,
                currentLoyaltyLevel: levelValue.toString()
            };

            const formattedRewardData = Rewards.fromJSON(modifiedRewardsOfferData);
            this.setRewards(formattedRewardData);
        } catch (err) {
            const errorMessage = err[0]?.message || '';
            this.logger.error({
                message: `Loyalty Reward API error : ${errorMessage}`,
                triggerType: TriggerType.api
            });
        }
    };

    @action public fetchBirthdayData = async (rewardOfferView: string) => {
        try {
            const rewardOfferData: ILoyaltyRewardsResponse =
                await this.apiSdk.getLoyaltyRewardOffers(rewardOfferView);

            const { levelValue } = this.loyaltyData;

            const modifiedRewardsOfferData = {
                ...rewardOfferData,
                currentLoyaltyLevel: levelValue.toString()
            };

            const formattedRewardData = Rewards.fromJSON(modifiedRewardsOfferData);
            this.setBirthdayRewards(formattedRewardData);
        } catch (err) {
            const errorMessage = err[0]?.message || '';
            this.logger.error({
                message: `Loyalty Reward API error : ${errorMessage}`,
                triggerType: TriggerType.api
            });
        }
    };

    @action public fetchBenefitsData = async (benefitOfferView: string) => {
        try {
            const benefitsOfferData: ILoyaltyBenefitsResponse =
                await this.apiSdk.getLoyaltyBenefitsOffers(benefitOfferView);

            const levelValue = this.loyaltyData?.levelValue ?? 0;

            const modifiedBenefitsOfferData = {
                ...benefitsOfferData,
                currentLoyaltyLevel: levelValue.toString()
            };

            const benefitData = Benefits.fromJSON(modifiedBenefitsOfferData);
            this.setBenefits(benefitData);
        } catch (err) {
            const errorMessage = err[0]?.message || '';
            this.logger.error({
                message: `Loyalty Benefits API error : ${errorMessage}`,
                triggerType: TriggerType.api
            });
        }
    };

    @action public setLoyaltyData = (loyaltyData: LoyaltyInfo) => {
        this.loyaltyData = loyaltyData;
    };

    @action public setPointHistory = (pointHistoryData: PointsHistory) => {
        this.pointHistoryData = pointHistoryData;
        if (this.enablePagination) {
            this.setPointsHistoryPaginationData();
        }
    };

    @action private getPaginationLimit = (limit: number) => {
        this.paginationLimit += limit;

        return this.paginationLimit;
    };

    @action public setPointsHistoryPaginationData(reset = false, paginationLimit = 5) {
        const pointsHistory: PointsHistory = { ...this.pointHistoryData};
        const totalRecords: number = pointsHistory?.pointsList?.length ?? 0;
        this.pointsHistoryPaginationData = pointsHistory ?? {};

        if (totalRecords > 0) {
            const showCount = this.getPaginationLimit(this.loadMoreLimit);
            if (reset) {
                this.paginationLimit = this.loadMoreLimit;
                this.pointsHistoryPaginationData.remainingRows = (totalRecords >= paginationLimit) ? (totalRecords - paginationLimit) : 0;
                this.pointsHistoryPaginationData.pointsList = this.pointsHistoryPaginationData.pointsList?.slice(0, paginationLimit);
            } else {
                this.pointsHistoryPaginationData.remainingRows = (totalRecords >= showCount) ? (totalRecords - showCount) : 0;
                this.pointsHistoryPaginationData.pointsList = this.pointsHistoryPaginationData.pointsList?.slice(0, showCount);
            }
        }
    }

    @action public setBenefits = (benefitData: Benefits) => {
        this.benefitData = benefitData;
    };

    @action public setRewards = (formattedRewardsData: Rewards) => {
        this.rewardsData = formattedRewardsData;
        this.rewardRedeemed = !!formattedRewardsData?.rewardsDataFormatted?.find(
            (data: AnyObject) => {
                return data?.inCart;
            }
        );
    };

    @action public setBirthdayRewards = (formattedRewardsData: Rewards) => {
        this.birthdayRewardsData = formattedRewardsData;
        this.birthdayRedeemed = !!formattedRewardsData?.rewardsDataFormatted?.find(
            (data: AnyObject) => {
                return data?.inCart;
            }
        );
    };

    @action public setBenefitsOffer = (
        appliedOfferData: ILoyaltyOfferResponse | IPGResponse,
        offerCode: string
    ) => {
        const benefitsRedeemedOffer: BenefitsOffer = this.enableCartGraphqlAPI
            ? this.formatGraphQLResponse(appliedOfferData as ILoyaltyOfferResponse)
            : this.formatRPCResponse(appliedOfferData as IPGResponse, offerCode);

        this.benefitRedeemOfferData = benefitsRedeemedOffer;
    };

    private formatGraphQLResponse(data: ILoyaltyOfferResponse): BenefitsOffer {
        return {
            inCartStatus: data?.success,
            cartItemsCount: data?.success,
            errorText: data?.errorDetail
        };
    }

    private formatRPCResponse(data: IPGResponse, offerCode: string): BenefitsOffer {
        return {
            inCartStatus: data?.result?.value?.inCart ? true : false,
            cartItemsCount: data?.result?.data?.trans_data?.offerCodes?.includes(offerCode) ? true : false,
            errorText: data?.result?.value?.error_message,
            cartItemQty: data?.result?.data?.trans_data?.items_count || 0,
            cartSubTotal: data?.result?.data?.trans_data?.subtotal || 0,
            cartFormattedSubTotal: data?.result?.data?.trans_data?.formattedSubTotal || '0'
        };
    }

    @action
    public setUserBirthdate = async (birthday: IBirthDate): Promise<void> => {
        await this.apiSdk.setUserBirthdate(birthday);
    };

    @action
    public setOfferRedemptionMessage = (params: IOfferRedemptionMessage) => {
        const { section, type, content, redemptionAction } = params;

        this.offerRedemptionMessages[section] = {
            type,
            content,
            section,
            redemptionAction
        };
    };

    @action
    clearOfferRedemptionMessage(section: IOfferRedemptionMessage['section']) {
        this.offerRedemptionMessages[section] = undefined;
    }

    @action
    public clearAllOfferRedemptionMessages() {
        Object.keys(this.offerRedemptionMessages).forEach(key => {
            this.offerRedemptionMessages[key as IOfferRedemptionMessage['section']] = undefined;
        });
    }

    @action
    public getOfferRedemptionMessage(section: IOfferRedemptionMessage['section']): IOfferRedemptionMessage | null {
        return this.offerRedemptionMessages[section] || null;
    }

    public async redeemOffer(
        applyOfferCode: ILoyaltyRedeemOfferRequest | ILoyaltyRPCRequest,
        offerView: string,
        loadOfferType: offerRedemptionType
    ) {

        if (this.enableCartGraphqlAPI) {
            await this.cartGraphqlAPICall(applyOfferCode, loadOfferType);
        } else {
            await this.rpcAPICall(applyOfferCode, loadOfferType);
        }

        if (Number(loadOfferType) === offerRedemptionType.birthday) {
            this.birthdayRedeemed = true;
        }
        if (Number(loadOfferType) === offerRedemptionType.rewards) {
            this.rewardRedeemed = true;
        }

        interface OfferFunctions {
            [key: number]: (offerView: string) => Promise<void>;
        }

        const offerFunctions: OfferFunctions = {
            [offerRedemptionType.birthday]: this.fetchBirthdayData,
            [offerRedemptionType.rewards]: this.fetchRewardsData,
            [offerRedemptionType.benefits]: this.fetchBenefitsData
        };

        const selectedFunction = offerFunctions[Number(loadOfferType)];

        if (selectedFunction) {
            await selectedFunction(offerView);
        }
    }

    public async cartGraphqlAPICall(
        applyOfferCode: ILoyaltyRedeemOfferRequest,
        offerType: offerRedemptionType
    ) {
        const { offerCode } = applyOfferCode;
        const applyOfferCodes: ILoyaltyRedeemOfferRequest[] = [];
        applyOfferCodes.push(applyOfferCode);

        // Below commented lines for example of  handling multiple offers in a single redeem query

        /*
        const applyOfferCode1 = {offerCode: "100_macstackmicromini", skuID: ["183343"]};
        const applyOfferCode2 = {offerCode: "lover_100_10off"};
        const applyOfferCodes = [applyOfferCode1, applyOfferCode2];
        applyOfferCodes.push(applyOfferCode1);
        applyOfferCodes.push(applyOfferCode2);
        */
        const redeemOffers: ILoyaltyRedeemOffersRequest = {
           redeemOffers: applyOfferCodes.map((value) => (
                {
                    offerCode : value?.offerCode,
                    ...(value?.isChoiceOfSku && value?.skuID && {skuBaseId: Array.isArray(value.skuID) ? value.skuID
                        : [value.skuID]})
                }
            )
           )
        };
        const appliedOfferResponse = await this.apiSdk.redeemOffer(redeemOffers);
        const appliedOfferResponseModified: ILoyaltyOfferResponseModifiedResponse = appliedOfferResponse?.redeemMultipleOffers?.reduce((acc, val) => ({
            ...acc,
            [val.userOfferCode || '']: val
        }), {});

        const offerResponseData = appliedOfferResponseModified[offerCode as string];

        const redeemOfferMessage = offerResponseData?.success ? offerResponseData?.userOfferSuccessMessage : offerResponseData?.userOfferErrorMessage;
        const redeemCommonOfferErrorMessage = !offerResponseData?.success && offerResponseData?.userCommonOfferErrorMessage ? offerResponseData?.userCommonOfferErrorMessage : '';
        const redeemOfferMessageType = offerResponseData?.success ? 'success' : 'error';
        const redeemOfferRedemptionType = this.getOfferRedemptionTypeString(offerType);

        this.setOfferRedemptionMessage({
            section: redeemOfferRedemptionType,
            type: redeemOfferMessageType,
            content: [redeemCommonOfferErrorMessage, redeemOfferMessage],
            redemptionAction: 'applied'
        });

        this.setBenefitsOffer(appliedOfferResponseModified[offerCode as string], offerCode as string);
    }

    public async rpcAPICall(
        applyOfferCode: ILoyaltyRPCRequest,
        offerType: offerRedemptionType
    ) {
        const { offerCode } = applyOfferCode;

        const appliedOfferResponse = await this.rpcApi.redeemOffer(applyOfferCode);
        const appliedOfferData = (appliedOfferResponse?.data || [])[0] || [];
        const skuOfferSuccessMessage = appliedOfferResponse?.skuOfferSuccessMessage;
        const redeemOfferRedemptionType = this.getOfferRedemptionTypeString(offerType);

        this.setOfferRedemptionMessage({
            section: redeemOfferRedemptionType,
            type: 'success',
            content: [skuOfferSuccessMessage],
            redemptionAction: 'applied'
        });

        this.setBenefitsOffer(appliedOfferData, offerCode as string);
    }

    public async removeOffer(
        offerCode: string,
        offerView: string,
        offerType: offerRedemptionType
    ) {
        if (this.enableCartGraphqlAPI) {
            const unredeemOffers: ILoyaltyUnredeemOfferApiRequest = {
                unredeemOffers: {
                    offerCode : offerCode as string
                }
            };
            const unredeemOfferResponse = await this.apiSdk.unredeemOffer(unredeemOffers);
            const unredeemedOffer = unredeemOfferResponse.unredeemOffer;
            const unredeemOfferMessage = unredeemedOffer?.userOfferRemovedMessage;
            const unredeemOfferMessageType = unredeemedOffer?.success ? 'success' : 'error';
            const unredeemOfferRedemptionType = this.getOfferRedemptionTypeString(offerType);

            this.setOfferRedemptionMessage({
                section: unredeemOfferRedemptionType,
                type: unredeemOfferMessageType,
                content: [unredeemOfferMessage],
                redemptionAction: 'removed'
            });
        } else {
            await this.rpcApi.removeOffer(offerCode);
        }

        switch (Number(offerType)) {
            case offerRedemptionType.birthday:
                await this.fetchBirthdayData(offerView);
                this.birthdayRedeemed = false;
                break;
            case offerRedemptionType.rewards:
                await this.fetchRewardsData(offerView);
                this.rewardRedeemed = false;
                break;
            case offerRedemptionType.benefits:
                await this.fetchBenefitsData(offerView);
                break;
            default:
        }
    }

    private getOfferRedemptionTypeString(offerType: offerRedemptionType): IOfferRedemptionMessage['section'] {
        switch (offerType) {
            case offerRedemptionType.birthday:
                return 'birthday';
            case offerRedemptionType.rewards:
                return 'rewards';
            case offerRedemptionType.benefits:
                return 'benefits';
            default:
                return 'benefits';
        }
    }

    @computed
    public get pointsHistory() {
        return (this.enablePagination) ? this.pointsHistoryPaginationData : this.pointHistoryData;
    }

    @computed
    public get benefitsOfferInfo() {
        return this.benefitRedeemOfferData;
    }

    @computed
    public get benefitsInfo() {
        return this.benefitData;
    }

    @computed
    public get rewardsInfo() {
        return this.rewardsData;
    }

    @computed
    public get birthdayRewardsInfo() {
        return this.birthdayRewardsData;
    }

    @computed
    public get hasEligibleBirthdayOffers() {
        if (this.birthdayRewardsData?.rewardsDataFormatted?.length >= 1) {
            let hasEligibleOffers = false;
            this.birthdayRewardsData?.rewardsDataFormatted.forEach(offer => {
                if (offer.criteriaViewValidations && offer.criteriaViewValidations.length > 0) {
                    let metCombinedCriterias = true;
                    offer.criteriaViewValidations.forEach(criteria => {
                        if (criteria.met === 0) {
                            metCombinedCriterias = false;
                        }
                    });

                    if (metCombinedCriterias) {
                        hasEligibleOffers = true;
                    }
                } else {
                    hasEligibleOffers = true;
                }
            });

            return hasEligibleOffers;
        } else {
            return false;
        }
    }

    @computed
    public get loyaltyInfo() {
        return this.loyaltyData;
    }

    @computed
    public get isLoyaltyUser() {
        return !!this.loyaltyInfo?.hasLoyalty;
    }

    @computed
    public get isOnlineUser() {
        return this.loyaltyInfo?.hasLoyalty === false;
    }

    @computed
    public get isRewardRedeemed() {
        return this.rewardRedeemed;
    }

    @computed
    public get isBirthdayRedeemed() {
        return this.birthdayRedeemed;
    }

    @computed
    public get offerRedemptionMessageData() {
        return this.offerRedemptionMessages;
    }
}
